import Box from '@mui/material/Box';
import List from '@mui/material/List';
import { useSidebar } from 'contexts/SidebarContext';
import useCurrentRoute from 'hooks/useCurrentRoute';
import type React from 'react';
import { useEffect, useRef } from 'react';

import NavButton from './NavButton';
import { GroupLabel } from './sidebar.styles';
import type { NavSection } from './sidebar.types';

interface SidebarNavProps {
  sections: NavSection[];
  onMobileClose: () => void;
}

const SidebarNav: React.FC<SidebarNavProps> = ({ sections, onMobileClose }) => {
  const { collapsed } = useSidebar();
  const { path } = useCurrentRoute();
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const isItemActive = (itemPath: string) => {
    if (!itemPath) return false;
    return path === itemPath || path.startsWith(`${itemPath}/`);
  };

  // Keep the active nav item fully visible — re-scroll whenever the scroll
  // container's size changes (e.g. footer inflates once business units load).
  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;
    const scrollActiveIntoView = () => {
      const active = container.querySelector<HTMLElement>('[data-nav-active="true"]');
      if (active) active.scrollIntoView({ block: 'nearest', behavior: 'auto' });
    };
    scrollActiveIntoView();
    const observer = new ResizeObserver(scrollActiveIntoView);
    observer.observe(container);
    return () => observer.disconnect();
  }, [path]);

  return (
    <Box ref={scrollRef} sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
      {sections.map((section) => {
        if (section.items.length === 0) return null;
        return (
          <Box key={section.label}>
            <GroupLabel
              sx={{
                textAlign: collapsed ? 'center' : 'left',
                pl: collapsed ? '4px' : '30px',
                pr: collapsed ? '4px' : '8px',
                letterSpacing: collapsed ? 0 : '0.05em',
                opacity: collapsed ? 0.72 : 1,
                transition: 'all 0.46s cubic-bezier(0.25, 0.8, 0.25, 1)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {section.label}
            </GroupLabel>
            <List disablePadding>
              {section.items.map((item) => (
                <NavButton
                  key={item.text}
                  item={item}
                  isActive={isItemActive(item.path)}
                  collapsed={collapsed}
                  onMobileClose={onMobileClose}
                />
              ))}
            </List>
          </Box>
        );
      })}
    </Box>
  );
};

export default SidebarNav;
